import React, { createContext, useCallback, useContext, useRef, useState } from "react";

export type ToastType = "success" | "error" | "info";

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

const TOAST_DURATION = 3200;

const TYPE_STYLES: Record<ToastType, string> = {
  success: "border-term-accent/40 text-term-accent",
  error: "border-term-error/40 text-term-error",
  info: "border-term-border text-term-text-dim",
};

const TYPE_BAR: Record<ToastType, string> = {
  success: "bg-term-accent",
  error: "bg-term-error",
  info: "bg-term-text-muted",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const toast = useCallback((message: string, type: ToastType = "success") => {
    const id = ++counter.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_DURATION);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        className="fixed top-4 right-4 z-[200] flex flex-col gap-2 items-end pointer-events-none"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`relative overflow-hidden flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium shadow-xl border bg-term-surface/85 backdrop-blur-md pointer-events-auto max-w-xs animate-[toast-in_0.18s_ease] ${TYPE_STYLES[t.type]}`}
          >
            {t.message}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-term-border/30">
              <div
                className={`h-full ${TYPE_BAR[t.type]} rounded-full`}
                style={{ animation: `toast-drain ${TOAST_DURATION}ms linear forwards` }}
              />
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
